// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardActions from '@mui/joy/CardActions';
import Grid from '@mui/joy/Grid';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Stack from '@mui/joy/Stack';
import Avatar from '@mui/joy/Avatar';
import Button from '@mui/joy/Button';
import LinearProgress from '@mui/joy/LinearProgress';

// Icons
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';

import { useInventory } from '../contexts/InventoryContext';
import { listSeeds as listAllSeeds } from '../api/allSeeds';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

export default function Dashboard() {
  const { listItems } = useInventory();

  // State
  const [allSeeds, setAllSeeds] = useState([]);
  const [invSeeds, setInvSeeds] = useState([]);
  const [invClones, setInvClones] = useState([]);
  const [stats, setStats] = useState({
    seeds: 0,
    clones: 0,
    packaged: 0,
    unpacked: 0,
    available: 0,
    inProgress: 0,
    seedValue: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [all, seedInv, cloneInv] = await Promise.all([
          listAllSeeds(),
          listItems('PRODUCT-SEED'),
          listItems('PRODUCT-CLONE')
        ]);

        // Ensure arrays are defined
        const allSeedsArray = all || [];
        const seedInvArray = seedInv || [];
        const cloneInvArray = cloneInv || [];

        setAllSeeds(allSeedsArray);
        setInvSeeds(seedInvArray);
        setInvClones(cloneInvArray);

        const seedsCount = allSeedsArray.filter(s => s.collection !== 'clones' && s.inventory > 0 && s.isActive).length;
        const clonesCount = allSeedsArray.filter(s => s.collection === 'clones' && s.inventory > 0 && s.isActive).length;
        const packagedCount = seedInvArray.reduce((sum, i) => sum + (i.packagedCount || 0), 0);
        const unpackedCount = seedInvArray.reduce((sum, i) => sum + (i.quantity || 0), 0);
        const availableClones = cloneInvArray.reduce((sum, i) => sum + (i.packagedCount || 0), 0);
        const inProgClones = cloneInvArray.reduce((sum, i) => sum + (i.quantity || 0), 0);
        const seedValue = packagedCount * 100 + Math.floor(unpackedCount / 10) * 100;

      setStats({
          seeds: seedsCount,
          clones: clonesCount,
          packaged: packagedCount,
          unpacked: unpackedCount,
          available: availableClones,
        inProgress: inProgClones,
        seedValue
      });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Set empty arrays to prevent crashes
        setAllSeeds([]);
        setInvSeeds([]);
        setInvClones([]);
        setStats({
          seeds: 0,
          clones: 0,
          packaged: 0,
          unpacked: 0,
          available: 0,
          inProgress: 0,
          seedValue: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [listItems]);

  // Metric configurations
  const metrics = [
    {
      id: 'value',
      title: 'Total Value',
      value: `$${stats.seedValue.toLocaleString()}`,
      icon: <AttachMoneyRoundedIcon />,
      color: 'success',
      gradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
      items: null
    },
    {
      id: 'seeds',
      title: 'Seed Varieties',
      value: stats.seeds.toLocaleString(),
      icon: <SpaRoundedIcon />,
      color: 'primary',
      gradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      items: allSeeds.filter(s => s.collection !== 'clones' && s.inventory > 0 && s.isActive)
    },
    {
      id: 'clones',
      title: 'Clone Varieties',
      value: stats.clones.toLocaleString(),
      icon: <OpacityRoundedIcon />,
      color: 'warning',
      gradient: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
      items: allSeeds.filter(s => s.collection === 'clones' && s.inventory > 0 && s.isActive)
    },
    {
      id: 'packaged',
      title: 'Packaged Seeds',
      value: stats.packaged.toLocaleString(),
      icon: <InventoryRoundedIcon />,
      color: 'neutral',
      gradient: 'linear-gradient(135deg, #607d8b 0%, #90a4ae 100%)',
      items: invSeeds.filter(i => (i.packagedCount || 0) > 0)
    },
    {
      id: 'unpacked',
      title: 'Loose Seeds',
      value: stats.unpacked.toLocaleString(),
      icon: <StorefrontRoundedIcon />,
      color: 'danger',
      gradient: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
      items: invSeeds.filter(i => (i.quantity || 0) > 0)
    },
    {
      id: 'available',
      title: 'Ready Clones',
      value: stats.available.toLocaleString(),
      icon: <TrendingUpRoundedIcon />,
      color: 'success',
      gradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
      items: invClones.filter(i => (i.packagedCount || 0) > 0)
    }
  ];

  const handleMetricClick = (metric) => {
    if (metric.items && metric.items.length > 0) {
      setSelectedMetric(metric);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
      }}>
        <Stack spacing={2} alignItems="center">
          <HomeRoundedIcon sx={{ fontSize: 48, color: '#ff9800' }} />
          <Typography level="h4" sx={{ color: '#ff9800' }}>Loading Dashboard...</Typography>
          <LinearProgress sx={{ width: 200 }} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      pt: 10,
      pb: 4,
      px: { xs: 2, sm: 3, md: 4 },
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    }}>
      {/* Header */}
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{ mb: 4 }}
      >
        <Sheet
          variant="soft"
        sx={{ 
          p: 3, 
            borderRadius: 'xl',
            background: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}
      >
            <Typography 
            level="h1" 
              sx={{ 
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 'bold',
              color: '#333333',
              mb: 1,
              textAlign: 'center'
            }}
          >
            Inventory Dashboard
            </Typography>
            <Typography 
            level="body-lg" 
            sx={{ 
              color: '#666666',
              textAlign: 'center',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Real-time overview of your genetics inventory
            </Typography>
        </Sheet>
      </MotionBox>

      {/* Metrics Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Grid container spacing={3}>
          {metrics.map((metric, index) => (
            <Grid key={metric.id} xs={12} sm={6} md={4}>
              <MotionCard
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.02,
                  transition: { type: 'spring', stiffness: 300, damping: 20 }
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleMetricClick(metric)}
              sx={{
                height: '100%',
                  background: metric.gradient,
                  cursor: (metric.items && metric.items.length > 0) ? 'pointer' : 'default',
                  border: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1
                  }
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                  <Stack direction="column" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar
                      variant="soft"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        width: 56,
                        height: 56,
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      {React.cloneElement(metric.icon, { sx: { fontSize: 28 } })}
              </Avatar>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography level="title-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)', textAlign: 'center' }}>
                        {metric.title}
              </Typography>
              <Typography 
                        level="h2" 
                sx={{ 
                          color: 'white',
                  fontWeight: 'bold', 
                          fontSize: { xs: '1.5rem', sm: '2rem' },
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                          textAlign: 'center'
                        }}
                      >
                        {metric.value}
              </Typography>
                    </Box>
                  </Stack>
                  
                  {(metric.items && metric.items.length > 0) && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Chip
                        variant="soft"
                        size="sm"
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          fontSize: '0.75rem',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        Click to view details
                      </Chip>
                    </Box>
                  )}
                </CardContent>
              </MotionCard>
          </Grid>
        ))}
      </Grid>
      </motion.div>

      {/* Full-Screen Detail Modal */}
      <Modal
        open={!!selectedMetric}
        onClose={() => setSelectedMetric(null)}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 0
        }}
      >
        <ModalDialog
          variant="soft"
          sx={{
            width: '100vw',
            height: '100vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 0,
            borderRadius: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.97) 0%, rgba(10, 10, 10, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: 'none',
            overflow: 'auto'
          }}
        >
          <ModalClose 
            sx={{
              position: 'fixed',
              top: 20,
              right: 20,
              zIndex: 1000,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              color: '#ff9800',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: 'lg',
              '&:hover': {
                bgcolor: 'rgba(255, 152, 0, 0.2)',
                transform: 'scale(1.05)'
              }
            }}
          />
          
          {selectedMetric && (
            <Box sx={{ p: 4, pt: 8 }}>
              {/* Enhanced Header */}
              <MotionBox
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
          sx={{
                  mb: 6, 
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                {/* Background Glow Effect */}
                <Box
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 300,
                    height: 300,
                    background: selectedMetric.gradient,
                    borderRadius: '50%',
                    opacity: 0.1,
                    filter: 'blur(100px)',
                    zIndex: 0
                  }}
                />
                
                <Stack 
                  direction="column" 
                  alignItems="center" 
                  spacing={3}
                  sx={{ position: 'relative', zIndex: 1 }}
                >
                  <Avatar 
                    variant="soft"
                    sx={{
                      width: 100, 
                      height: 100,
                      background: selectedMetric.gradient,
                      border: '3px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -3,
                        left: -3,
                        right: -3,
                        bottom: -3,
                        background: selectedMetric.gradient,
                        borderRadius: '50%',
                        opacity: 0.3,
                        filter: 'blur(8px)',
                        zIndex: -1
                      }
                    }}
                  >
                    {React.cloneElement(selectedMetric.icon, { sx: { fontSize: 48, color: 'white' } })}
                  </Avatar>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      level="h1" 
                      sx={{ 
                        color: 'white', 
                        mb: 1, 
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        fontWeight: 'bold',
                        textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {selectedMetric.title}
                    </Typography>
                    <Typography 
                      level="h2" 
                      sx={{ 
                        background: selectedMetric.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                        fontSize: { xs: '2rem', md: '3rem' },
                        fontWeight: 'bold',
                        textShadow: '0 2px 10px rgba(255, 152, 0, 0.3)'
                      }}
                    >
                      {selectedMetric.value}
                    </Typography>
                    <Typography 
                      level="body-lg" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '1.2rem'
                      }}
                    >
                      {(selectedMetric.items && selectedMetric.items.length) || 0} items total
                    </Typography>
                  </Box>
                </Stack>
              </MotionBox>

              {/* Enhanced Grid */}
              <Grid container spacing={4}>
                <AnimatePresence>
                  {(selectedMetric.items || []).map((item, index) => (
                    <Grid key={item.id || item.itemId} xs={12} sm={6} md={4} lg={3}>
                      <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -30, scale: 0.9 }}
                        transition={{ 
                          delay: index * 0.05,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20
                        }}
                        whileHover={{ 
                          scale: 1.03,
                          transition: { type: 'spring', stiffness: 400, damping: 10 }
                        }}
                        style={{ height: '100%' }}
                      >
                        <Card
                          variant="soft"
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: 'xl',
                            overflow: 'hidden',
                            position: 'relative',
                            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                              border: '1px solid rgba(255, 152, 0, 0.4)',
                              '&::before': {
                                opacity: 1
                              }
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              zIndex: 0
                            }
                          }}
                        >
                          {/* Enhanced Image Section */}
                          {(item.imageUrl || (item.imageUrls && item.imageUrls.length > 0 && item.imageUrls[0])) && (
                            <Box
                              sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 'lg lg 0 0'
                    }}
                  >
                    <Box
                      component="img"
                                src={item.imageUrl || (item.imageUrls && item.imageUrls.length > 0 && item.imageUrls[0])}
                      alt={item.name}
                      sx={{
                                  width: '100%',
                                  height: 200,
                        objectFit: 'cover',
                                  transition: 'transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                  '&:hover': {
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              />
                              {/* Image Overlay */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.2) 100%)',
                                  opacity: 0,
                                  transition: 'opacity 0.3s ease',
                                  '.MuiCard-root:hover &': {
                                    opacity: 1
                                  }
                                }}
                              />
                            </Box>
                          )}

                          <CardContent 
                            sx={{ 
                              p: 3, 
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              zIndex: 1
                            }}
                          >
                            {/* Enhanced Content Header */}
                            <Box sx={{ mb: 3 }}>
                      <Typography 
                                level="title-lg" 
                        sx={{ 
                                  color: 'white', 
                          mb: 1,
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  lineHeight: 1.3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textAlign: 'center'
                                }}
                              >
                                {item.name}
                              </Typography>
                              
                              {item.description && (
                                <Typography 
                                  level="body-sm" 
                                  sx={{ 
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 2,
                          display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                                    lineHeight: 1.5,
                                    minHeight: '4.5em',
                                    textAlign: 'center'
                        }}
                      >
                                  {item.description}
                      </Typography>
                              )}
                    </Box>
                            
                            {/* Enhanced Stats Chips */}
                            <Box sx={{ mt: 'auto' }}>
                              <Stack 
                                direction="row" 
                                spacing={1} 
                                flexWrap="wrap" 
                                sx={{ 
                                  gap: 1,
                                  justifyContent: 'center'
                                }}
                              >
                                {item.inventory !== undefined && (
                    <Chip
                                    size="sm" 
                                    variant="soft" 
                      sx={{
                                      bgcolor: item.inventory > 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)', 
                                      color: item.inventory > 0 ? '#66bb6a' : '#ff9800',
                                      border: item.inventory > 0 ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(255, 152, 0, 0.3)',
                        fontWeight: 'bold',
                                      '&:hover': {
                                        bgcolor: item.inventory > 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 152, 0, 0.3)'
                                      }
                                    }}
                                  >
                                    Stock: {item.inventory.toLocaleString()}
                                  </Chip>
                                )}
                                {item.quantity !== undefined && (
                                  <Chip
                                    size="sm" 
                                    variant="soft" 
                                    sx={{
                                      bgcolor: item.quantity > 0 ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 152, 0, 0.15)', 
                                      color: item.quantity > 0 ? '#ffffff' : '#ff9800',
                                      border: item.quantity > 0 ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 152, 0, 0.2)',
                                      fontWeight: 'bold',
                                      '&:hover': {
                                        bgcolor: item.quantity > 0 ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 152, 0, 0.25)'
                                      }
                                    }}
                                  >
                                    Qty: {item.quantity.toLocaleString()}
                                  </Chip>
                                )}
                                {item.packagedCount !== undefined && (
                                  <Chip 
                                    size="sm" 
                                    variant="soft" 
            sx={{
                                      bgcolor: item.packagedCount > 0 ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)', 
                                      color: '#ff9800',
                                      border: '1px solid rgba(255, 152, 0, 0.3)',
              fontWeight: 'bold',
              '&:hover': {
                                        bgcolor: 'rgba(255, 152, 0, 0.3)'
                                      }
                                    }}
                                  >
                                    Packaged: {item.packagedCount.toLocaleString()}
                                  </Chip>
                                )}
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
}
